import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, map, tap, switchMap, finalize } from 'rxjs/operators';
import { environment } from '@env/environment';
import {
  LeadResumeResponse,
  LeadResumeRequest,
  LeadResumeStatus,
  LeadResumeState,
  ProductsConfigurationData
} from '@app/models/lead-resume';
import { AuthService } from '../auth/auth.service';
import { Store } from '@ngrx/store';
import { setData } from 'src/storage/general.actions';

/**
 * Servicio para gestionar la recuperación y manejo de leads desde el orquestador
 * Utiliza Signals de Angular 17 para un manejo reactivo del estado
 */
@Injectable({
  providedIn: 'root'
})
export class LeadResumeService {
  // Inyección de dependencias moderna de Angular 17
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store);

  // URL del endpoint del orquestador para lead resume
  private readonly LEAD_RESUME_ENDPOINT = `${environment.URL_MULESOFT_ORQUESTADOR}`;

  // Estado reactivo usando Signals (Angular 17+)
  private readonly _state = signal<LeadResumeState>({
    status: LeadResumeStatus.IDLE,
    data: null,
    error: null,
    lastUpdated: null
  });

  // Signals computados para acceso reactivo
  public readonly state = this._state.asReadonly();
  public readonly isLoading = computed(() => this._state().status === LeadResumeStatus.LOADING);
  public readonly hasError = computed(() => this._state().status === LeadResumeStatus.ERROR);
  public readonly hasData = computed(() => this._state().status === LeadResumeStatus.SUCCESS && this._state().data !== null);
  public readonly leadData = computed(() => this._state().data?.data.productsconfigurationdataarriendos ?? null);
  public readonly error = computed(() => this._state().error);

  // BehaviorSubject para compatibilidad con código existente que usa observables
  private readonly _leadData$ = new BehaviorSubject<ProductsConfigurationData | null>(null);
  public readonly leadData$ = this._leadData$.asObservable();


  /**
   * Obtiene los datos del lead desde el orquestador
   * @param idLeadParameter - ID del lead sin decodificar
   * @returns Observable con la respuesta del orquestador
   */
  public getLeadResume(idLeadParameter: string): Observable<LeadResumeResponse> {
    console.log('📡 Iniciando recuperación de lead...');

    // Actualizar estado a loading
    this._updateState({ status: LeadResumeStatus.LOADING });

    // Crear el request body con la nueva estructura
    const requestBody: LeadResumeRequest = {
      getRetakeData: true,
      recordId: idLeadParameter,
      productCategory: 'Arriendos'
    };

    // Realizar petición autenticada
    return this.authService.createAuthHeaders().pipe(
      switchMap(headers => {
        console.log('🔑 Headers de autenticación creados');
        return this.http.post<LeadResumeResponse>(
          this.LEAD_RESUME_ENDPOINT,
          requestBody,
          { headers }
        );
      }),
      tap(response => {
        console.log('✅ Lead recuperado exitosamente');
        this._updateState({
          status: LeadResumeStatus.SUCCESS,
          data: response,
          error: null,
          lastUpdated: new Date()
        });
        // Actualizar BehaviorSubject para compatibilidad
        this._leadData$.next(response.data.productsconfigurationdataarriendos);

        // Guardar el leadId en el store si viene en la respuesta
        if (response.data.productsconfigurationdataarriendos.leadId) {
          console.log('💾 Guardando leadId en el store:', response.data.productsconfigurationdataarriendos.leadId);
          this.store.dispatch(setData({
            key: 'leadId',
            value: response.data.productsconfigurationdataarriendos.leadId,
            merge: false
          }));
        }
      }),
      catchError(error => {
        console.error('❌ Error al recuperar lead:', error);
        const errorMessage = this._extractErrorMessage(error);
        this._updateState({
          status: LeadResumeStatus.ERROR,
          error: errorMessage,
          data: null
        });
        return throwError(() => new Error(errorMessage));
      }),
      finalize(() => {
        console.log('🏁 Proceso de recuperación de lead finalizado');
      })
    );
  }

  /**
   * Verifica si existe un idLead en los parámetros de la URL
   * @param url - URL completa o search params
   * @returns ID del lead encriptado o null
   */
  public extractLeadIdFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url, window.location.origin);
      const leadId = urlObj.searchParams.get('recordId');

      if (leadId) {
        console.log('🔍 recordId encontrado en URL');
      }

      return leadId;
    } catch (error) {
      console.error('❌ Error al extraer recordId de la URL:', error);
      return null;
    }
  }

  /**
   * Limpia el estado del servicio
   */
  public clearState(): void {
    console.log('🧹 Limpiando estado del lead resume');
    this._updateState({
      status: LeadResumeStatus.IDLE,
      data: null,
      error: null,
      lastUpdated: null
    });
    this._leadData$.next(null);
  }

  /**
   * Verifica si hay datos cargados actualmente
   */
  public hasLoadedData(): boolean {
    return this._state().status === LeadResumeStatus.SUCCESS && this._state().data !== null;
  }

  /**
   * Obtiene los datos cargados actualmente (snapshot)
   */
  public getCurrentData(): ProductsConfigurationData | null {
    return this._state().data?.data.productsconfigurationdataarriendos ?? null;
  }

  /**
   * Actualiza el estado interno del servicio
   * @param updates - Actualizaciones parciales del estado
   */
  private _updateState(updates: Partial<LeadResumeState>): void {
    this._state.update(current => ({
      ...current,
      ...updates
    }));
  }

  /**
   * Extrae el mensaje de error de diferentes tipos de respuestas
   * @param error - Error capturado
   * @returns Mensaje de error formateado
   */
  private _extractErrorMessage(error: any): string {
    if (error?.error?.message) {
      return error.error.message;
    }
    if (error?.message) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'Error al recuperar la información del lead';
  }
}
